"use client";
import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TextField,
  IconButton,
  InputAdornment,
  Stack,
  Chip,
  TablePagination,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000";
const REPORT_ENDPOINT = `${API_BASE}/rockstar/admin/referrals-report`;

// small util to format date/time
function formatDateTime(val) {
  if (!val) return "-";
  try {
    return new Date(val).toLocaleString();
  } catch {
    return String(val);
  }
}

// CSV export util (simple)
function exportCsv(filename, rows, columns) {
  const header = columns.map(c => `"${c.label.replace(/"/g, '""')}"`).join(",");
  const lines = rows.map(r =>
    columns
      .map(c => {
        const v = typeof c.key === "function" ? c.key(r) : r[c.key];
        return `"${(v ?? "").toString().replace(/"/g, '""')}"`;
      })
      .join(",")
  );
  const csv = [header].concat(lines).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminReferrals() {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  // UI controls
  const [query, setQuery] = useState("");
  const [month, setMonth] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const token =
    typeof window !== "undefined" ? (sessionStorage.getItem("admin_token") || localStorage.getItem("admin_token")) : null;

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line
  }, []);

  async function fetchReport(opts = {}) {
    setLoading(true);
    setError(null);
    try {
      if (!token) {
        setError("Not authenticated");
        setReport(null);
        setLoading(false);
        return;
      }

      const q = new URL(REPORT_ENDPOINT);
      if (month) q.searchParams.set("month", month);
      // backend supports referrerId if needed: q.searchParams.set('referrerId', ...)
      const res = await fetch(q.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.message || "Failed to fetch report");
      const byReferrer = body?.data?.byReferrer || [];
      const totalsByReferrer = body?.data?.totalsByReferrer || [];
      const rows = body?.data?.rows || [];
      // compute monthlyTotals from byReferrer
      const monthMap = new Map();
      for (const r of byReferrer) {
        const m = r.month || "N/A";
        monthMap.set(m, (monthMap.get(m) || 0) + Number(r.cnt || 0));
      }
      const monthlyTotals = Array.from(monthMap.entries())
        .map(([m, total]) => ({ month: m, total }))
        .sort((a, b) => (b.month || "").localeCompare(a.month || ""));
      setReport({ byReferrer, totalsByReferrer, rows, monthlyTotals });
    } catch (err) {
      console.error("fetchReport err", err);
      setError(err.message || "Failed to load report");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }

  // filtering for per-referrer table
  const filteredByReferrer = useMemo(() => {
    if (!report?.byReferrer) return [];
    if (!query) return report.byReferrer;
    const q = query.trim().toLowerCase();
    return report.byReferrer.filter((r) => {
      return (
        String(r.referrerRockstarId || "").toLowerCase().includes(q) ||
        String(r.referrerName || "").toLowerCase().includes(q) ||
        String(r.referrerEmail || "").toLowerCase().includes(q) ||
        String(r.referralCode || "").toLowerCase().includes(q)
      );
    });
  }, [report, query]);

  // pagination for recent referrals (rows)
  const recentRows = report?.rows || [];
  const recentPageRows = recentRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // columns for CSV export
  const csvCols = [
    { key: "referrerRockstarId", label: "Referrer Rockstar ID" },
    { key: "referrerName", label: "Referrer Name" },
    { key: "referrerEmail", label: "Referrer Email" },
    { key: "referralCode", label: "Referral Code" },
    { key: "month", label: "Month" },
    { key: (r) => (r.cnt ?? 0), label: "Count" },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, gap: 2, alignItems: "center" }}>
          <Typography variant="h6">Referrals Report</Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Search referrer / code / email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <TextField
              size="small"
              label="Month (YYYY-MM)"
              placeholder="2025-10"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              sx={{ width: 160 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterListIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Refresh">
              <IconButton color="primary" onClick={() => fetchReport()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <>
            {/* Monthly Totals */}
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
              Monthly Totals
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "action.hover" }}>
                    <TableCell>Month</TableCell>
                    <TableCell>Total Referrals</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(report?.monthlyTotals && report.monthlyTotals.length > 0) ? (
                    report.monthlyTotals.map((row) => (
                      <TableRow key={row.month} hover sx={{ "&:nth-of-type(odd)": { backgroundColor: "background.paper" } }}>
                        <TableCell>{row.month}</TableCell>
                        <TableCell>{row.total}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        No data
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Per-referrer monthly counts */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle1">Per-referrer monthly counts</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={() => exportCsv("referrer_counts.csv", filteredByReferrer, csvCols)}
                >
                  Export CSV
                </Button>
                <Chip label={`${filteredByReferrer.length} rows`} size="small" />
              </Stack>
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: 340, mb: 3 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "action.hover" }}>
                    <TableCell>Referrer (Rockstar ID)</TableCell>
                    <TableCell>Referral Code</TableCell>
                    <TableCell>Month</TableCell>
                    <TableCell>Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredByReferrer.length > 0 ? (
                    filteredByReferrer.map((r, idx) => (
                      <TableRow
                        key={`${r.referrerId}-${r.referralCode || "na"}-${r.month || "na"}-${idx}`}
                        hover
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: "background.default" },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {r.referrerRockstarId || `#${r.referrerId}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.referrerName || r.referrerEmail || ""}
                          </Typography>
                        </TableCell>
                        <TableCell>{r.referralCode || "-"}</TableCell>
                        <TableCell>{r.month || "-"}</TableCell>
                        <TableCell>
                          <Chip label={r.cnt} size="small" color="primary" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No data
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Totals per referrer */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Total referrals per referrer
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "action.hover" }}>
                      <TableCell>Referrer (Rockstar ID)</TableCell>
                      <TableCell>Referrer Name / Email</TableCell>
                      <TableCell>Total Referrals</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(report?.totalsByReferrer && report.totalsByReferrer.length > 0) ? (
                      report.totalsByReferrer.map((r) => (
                        <TableRow key={r.referrerId} hover>
                          <TableCell>{r.referrerRockstarId || `#${r.referrerId}`}</TableCell>
                          <TableCell>{r.referrerName || r.referrerEmail || "-"}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {r.total}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No data
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Recent referrals with pagination */}
            <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="subtitle1">Recent referrals</Typography>
              <Typography variant="caption" color="text.secondary">
                Showing {recentRows.length} total
              </Typography>
            </Box>

            <TableContainer component={Paper} sx={{ mb: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "action.hover" }}>
                    <TableCell>Referrer</TableCell>
                    <TableCell>Referred</TableCell>
                    <TableCell>Referrer Code</TableCell>
                    <TableCell>Used At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentPageRows.length > 0 ? (
                    recentPageRows.map((r) => (
                      <TableRow key={r.id || `${r.referrerId}-${r.referredId}-${r.usedAt}`}>
                        <TableCell>{r.referrerRockstarId || `${r.referrerName || ""} (#${r.referrerId})`}</TableCell>
                        <TableCell>{r.referredName || r.referredRockstarId || `#${r.referredId}`}</TableCell>
                        <TableCell>{r.referrerCode || "-"}</TableCell>
                        <TableCell>{formatDateTime(r.usedAt)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No recent referrals
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={recentRows.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </Paper>
    </Container>
  );
}
